import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "aws-amplify";
import { nanoid } from "nanoid";
import { wait } from "./../../utils/wait";
import { BriefType } from "./types";

import * as Sentry from "@sentry/nextjs";

//sentry error tracking

const getCloudFrontUrl = () => {
  if (
    process.env.NEXT_PUBLIC_BRANCH === "dev" ||
    process.env.NODE_ENV === "development"
  ) {
    // https://dbw9gybhzqka8.cloudfront.net/dev
    return "https://d3j2ta27rwrdzu.cloudfront.net/staging";
  }
  if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
    return "https://d3j2ta27rwrdzu.cloudfront.net/staging";
  }
  return "https://d1zkfz8hmolxzx.cloudfront.net/production";
};

export const getCompetitorsAsync = createAsyncThunk(
  "briefs/getCompetitorsAsync",
  async ({ id }: { id: string }) => {
    const request = async (retry: number) => {
      if (retry > 60) {
        throw new Error("retry limit reached");
      }
      try {
        const { data } = await API.get(
          "crawler",
          `/crawler/competitors-async?id=${id}`,
          {}
        );

        if (data.status === "pending") {
          await wait(10000);
          return request(retry + 1);
        }

        return data;
      } catch (err) {
        //send error to sentry
        console.log(err);
        Sentry.captureException(err);

        throw err;

        //
      }
    };

    const result = await request(1);
    return result;
  }
);

export const getSerpCompetitors = createAsyncThunk(
  "briefs/getSerpCompetitors",
  async ({
    keyword,
    locationName,
    languageCode,
    id,
  }: {
    keyword: string;
    id: string;
    locationName: string;
    languageCode: string;
  }) => {
    const request = async (retry: number) => {
      if (retry > 4) {
        throw new Error("retry limit reached");
      }
      try {
        const data = await API.get(
          "serps",
          `/serps/competitors?keyword=${encodeURIComponent(
            keyword
          )}&locationName=${locationName}&languageCode=${languageCode}&id=${id}`,
          {}
        );
        return data;
      } catch (err) {
        Sentry.captureException(err);
        console.log("Error in getSerpCompetitors:", err);

        // Add safety check for err.response
        if (err && err.response && err.response.status) {
          if (Number(err.response.status) === 504) {
            await wait(10000);
            return await request(retry + 1);
          }
        }

        // Check if it's a network error - network errors often don't have response object
        if (err && err.message && err.message.includes("Network Error")) {
          console.log("Network error detected, retrying...");
          await wait(10000);
          return await request(retry + 1);
        }

        // Return a graceful error object if we can't retry
        return { error: true, message: err?.message || "An error occurred" };
      }
    };

    const result = await request(1);
    return result;
  }
);

// get stats
export const getStats = createAsyncThunk(
  "briefs/getStats",
  async ({ keyword, briefId }:
    { keyword: string; briefId: string }) => {
    const request = async (retry: number) => {
      if (retry > 4) {
        throw new Error("retry limit reached");
      }
      try {
        const data = await API.post(
          "brief",
          `/briefs/stats`,
          {
            body: { keyword, briefId },
          }
        );
        return data;
      } catch (err) {
        Sentry.captureException(err);
        console.log(err);

        // Add safety check for err.response
        if (err && err.response && err.response.status) {
          if (Number(err.response.status) === 504) {
            await wait(20000);
            return await request(retry + 1);
          } else if (Number(err.response.status) === 505) {
            await wait(20000);
            return await request(retry + 1);
          }
        }

        // Return a graceful error object if we can't retry
        return { error: true, message: err?.message || "An error occurred" };
      }
    }
    const result = await request(1);
    return result;
  }
);


// secondary keywords task
// export const getSerpCompetitorsTopics = createAsyncThunk(
//   "briefs/getSerpCompetitorsTopics",
//   async ({ id }: { id: string }) => {
//     const request = async (retry: number) => {
//       if (retry > 4) {
//         throw new Error("retry limit reached");
//       }
//       try {
//         const data = await API.get(
//           "serps",
//           `/serps/competitors-topics?id=${id}`,
//           {}
//         );
//         if (data.status === "pending") {
//           await wait(10000);
//           return await request(retry + 1);
//         }
//         return data;
//       } catch (err) {
//         await wait(10000);
//         return await request(retry + 1);
//       }
//     };

//     const result = await request(1);
//     return result;
//   }
// );

export const getQuestions = createAsyncThunk(
  "briefs/getQuestions",
  async ({
    locationName,
    languageCode,
    site,
    keyword,
  }: {
    keyword: string;
    briefId: string;
    locationName: string;
    languageCode: string;
    site: string;
  }) => {
    try {
      const data = await API.get(
        "serps",
        `/serps/questions?keyword=${encodeURIComponent(
          keyword
        )}&locationName=${locationName}&languageCode=${languageCode}&site=${site}`,
        {}
      );
      return data;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getAllBriefData = createAsyncThunk(
  "briefs/briefAsync",
  async (id: string) => {
    const url = getCloudFrontUrl();
    try {
      const response = await fetch(`${url}/brief-async?id=${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      Sentry.captureException(err);
      console.log("Error fetching brief data:", err);
      throw err;
    }
  }
);

export const briefUpdated = createAsyncThunk(
  "briefs/briefUpdated",
  async ({
    description,
    selectedOutlines,
    id,
    selectedTopics,
    title,
    wordCount,
    showHeadingsDialog,
    isPublic,
    images,
    headings,
    isNew,
    isArchived,
    insights,
    companyDetails,
    stats,
    interlinking,
    statistics,
    humanize,
    isBriefCompleted,
    isOnboardingBrief,
    createdAt,
    retry,
    thoughtLeadership,
    aiEvalRefineAgent,
    aiResearchAgent

  }: Partial<BriefType> & { id: string }) => {
    try {
      const { data } = await API.put("brief", `/briefs/${id}`, {
        body: {
          description,
          selectedTopics,
          title,
          wordCount,
          showHeadingsDialog,
          isPublic,
          images,
          headings,
          isArchived,
          isNew,
          insights,
          companyDetails,
          stats,
          interlinking,
          statistics,
          humanize,
          isBriefCompleted,
          isOnboardingBrief,
          createdAt,
          retry,
          thoughtLeadership,
          aiEvalRefineAgent,
          aiResearchAgent,
          selectedOutlines: selectedOutlines
            ?.filter((outline) => outline.text)
            .map((outline) => ({
              id: outline.id,
              text: outline.text,
              tag: outline.tag,
              index: outline.index,
              urls: outline.urls,
              takeaways: outline.takeaways,
              indent: outline.indent,
              similarityMapping: outline.similarityMapping,
              headingPriority: outline.headingPriority,
              headerContentKeywords: outline.headerContentKeywords?.filter(
                (keyword) => keyword.priority !== "0.00"
              ),
            })),
        },
      });
      return data.Attributes;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const expandCompetitorOutline = createAsyncThunk(
  "briefs/expandCompetitorOutline",
  async (id: number) => {
    try {
      return {
        id,
        headings: [].map((heading) => ({
          ...heading,
          id: nanoid(),
        })),
      };
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const createBrief = createAsyncThunk<
  BriefType,
  {
    keyword: string;
    locationName: string;
    languageCode: string;
    languageName: string;
    projectId: string;
    projectName: string;
    seoMissionsBriefId: string;
    isOnboardingBrief: boolean;
    generateArticle: boolean;
    GSCsiteUrl: string;
    companyDetails: boolean;
    stats: boolean;
    insights: string;
    generateAutomatedInsights: boolean;
    interlinking: boolean;
    humanize: boolean;
    thoughtLeadership: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
    articleTitle?: string;
  }
>(
  "briefs/create",
  async ({ keyword, locationName, languageCode, languageName, projectId, projectName, seoMissionsBriefId, isOnboardingBrief, generateArticle, GSCsiteUrl, companyDetails, stats, insights, generateAutomatedInsights, interlinking, humanize, thoughtLeadership, aiResearchAgent, aiEvalRefineAgent, articleTitle }) => {
    try {
      const response = await API.post("brief", "/briefs", {
        body: { keyword, locationName, languageCode, languageName, projectId, projectName, seoMissionsBriefId, isOnboardingBrief, generateArticle, GSCsiteUrl, companyDetails, stats, insights, generateAutomatedInsights, interlinking, humanize, thoughtLeadership, aiResearchAgent, aiEvalRefineAgent, articleTitle: articleTitle || '' },
      });
      return (response.data ?? response ?? {}) as BriefType;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
      return;
    }
  }
);

//retpry brief
export const retryBrief = createAsyncThunk(
  "briefs/retryBrief",
  async ({ id }: { id: string }) => {
    try {
      return await API.post("brief", `/briefs/retry/${id}`, {
        body: {
          createdAt: new Date().toISOString(),
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//check brief status
export const checkBriefStatus = createAsyncThunk(
  "briefs/checkBriefStatus",
  async ({ id }: { id: string }) => {
    try {

      if (!id) {
        return {
          message: "No id provided",
        }
      }
      const response = await API.get("brief", `/briefs/status/${id}`, {});

      return response;
    } catch (err) {
      console.error(`Error in checkBriefStatus for id: ${id}`, err);
      console.error(`Error type: ${err?.name}, message: ${err?.message}`);
      // Add safety check for err.response
      if (err && err.response) {
        console.error(`Response status: ${err.response.status}`);
        console.error(`Response data:`, err.response.data);
      }
      Sentry.captureException(err);
      throw err;
    }
  }
);
export const briefsRequested = createAsyncThunk(
  "briefs/requested",
  async ({
    limit,
    page,
    keyword,
    project,
  }: {
    limit: number;
    keyword?: string;
    project?: string;
    page?: number;

  }) => {
    try {

      const paramData = `limit=${limit}${page ? `&page=${encodeURIComponent(page)}` : '&page=0'} ${project ? `&project=${encodeURIComponent(project)}` : ""}${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ""}`;

      const data = await API.get(
        "brief",
        `/briefs?${paramData}`,
        {}
      );

      return data;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const singleBriefRequested = createAsyncThunk(
  "briefs/singleObjectRequested",
  async ({ id, logout }: { logout: () => void; id: string }) => {
    try {
      const data = await API.get("brief", `/briefs/object/${id}`, {});
      return data;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
      // Add safety check for err.response
      if (err && err.response && err.response.status === 403) {
        logout();
      }
      // Return a graceful error object
      return { error: true, message: err?.message || "An error occurred" };
    }
  }
);

export const archiveBriefRequested = createAsyncThunk(
  "briefs/archiveBriefRequested",
  async ({ id }: { id: string }) => {
    try {
      return await API.put("brief", `/briefs/${id}`, {
        body: { isArchived: true },
      });
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const archivedBriefsRequested = createAsyncThunk(
  "briefs/archivedBriefsRequested",
  async ({
    limit,
    page,
    keyword,
    project
  }: {
    limit: number;
    keyword?: string;
    project?: string;
    page?: number;

  }) => {
    try {
      const paramData = `isArchived=true&limit=${limit}${page ? `&page=${encodeURIComponent(page)}` : '&page=0'}  ${project ? `&project=${encodeURIComponent(project)}` : ""}${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ""}`;
      const data = await API.get(
        "brief",
        `/briefs?${paramData}`,
        {}
      );
      return data;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const unarchiveBriefRequested = createAsyncThunk(
  "briefs/unarchiveBriefRequested",
  async ({ id }: { id: string }) => {
    try {
      return await API.put("brief", `/briefs/${id}`, {
        body: { isArchived: false },
      });
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const deleteBriefRequested = createAsyncThunk(
  "briefs/deleteBriefRequested",
  async ({ id }: { id: string }) => {
    try {
      return await API.del("brief", `/briefs/object/${id}`, {});
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);
//update brief status
export const updateBriefStatus = createAsyncThunk(
  "briefs/updateBriefStatus",
  async ({ id, isBriefCompleted }: { id: string; isBriefCompleted: boolean }) => {
    try {
      return await API.post("brief", `/briefs/updateStatus`, {
        body: {
          id,
          isBriefCompleted,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);